import * as React from "react";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components/index";
import { withTranslation, WithTranslation } from "react-i18next";
import { ModalContent, Input, SelectAdv, Button, FormGroup } from "@lib/components";
import { styled } from "@lib/components";
import { ModalDropContent } from "@lib/components";
import { CustomerModalAgeVerification } from "./age-verification";
import { UI } from "../../../../../core/ui";
import { logger } from "@lib/common";
import { DetailRowList } from "@lib/components";
import { RestaurantItemModal } from "../../../common/item-modal";
import { Select } from "@lib/components";
import { Untrusive } from "@lib/common";
import { cloneDeepSafe } from "@lib/common";
import { CW_BOOKING_PREFILL_STORAGE_KEY } from "../bookings/booking-create-modal";
import { FaCalendarAlt } from "react-icons/fa";

interface Props extends WithTranslation { }
interface State {
	tagsSelected: string[];
	newTagDraft: string;
	notesDraft: string;
}

const HeroShell = styled.div`
	padding: 1.25rem 1.35rem 1.1rem;
	background: linear-gradient(180deg, #fafbfc 0%, #f8fafc 100%);
	border-bottom: 1px solid #f1f5f9;
`;

const HeroTop = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 1rem;
`;

const AvatarTile = styled.div`
	width: 4.25rem;
	height: 4.25rem;
	border-radius: 1.25rem;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(145deg, #6366f1 0%, #4f46e5 55%, #4338ca 100%);
	color: #fff;
	font-size: 1.125rem;
	font-weight: 800;
	letter-spacing: -0.03em;
	box-shadow: 0 12px 28px -10px rgba(79, 70, 229, 0.55);
	overflow: hidden;
`;

const HeroTitleBlock = styled.div`
	flex: 1;
	min-width: 0;
`;

const HeroName = styled.h3`
	margin: 0;
	font-size: 1.35rem;
	font-weight: 800;
	color: #0f172a;
	letter-spacing: -0.02em;
	line-height: 1.2;
`;

const HeroMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem;
	margin-top: 0.35rem;
`;

const VerifiedPill = styled.span`
	display: inline-block;
	padding: 0.15rem 0.45rem;
	border-radius: 6px;
	font-size: 0.5625rem;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.12em;
	background: #eef2ff;
	color: #4338ca;
	border: 1px solid #e0e7ff;
`;

const ReserveCta = styled.button`
	width: 100%;
	margin-top: 1rem;
	padding: 0.9rem 1rem;
	border: none;
	border-radius: 1rem;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	font-size: 0.6875rem;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.12em;
	color: #fff;
	background: linear-gradient(180deg, #4f46e5 0%, #4338ca 100%);
	box-shadow: 0 12px 28px -8px rgba(79, 70, 229, 0.45);
	transition: transform 0.12s ease, box-shadow 0.12s ease;
	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 16px 32px -8px rgba(79, 70, 229, 0.5);
	}
`;

const CrmCard = styled.div`
	padding: 1.1rem 1.15rem;
	margin-bottom: 0.85rem;
	border-radius: 1.25rem;
	border: 1px solid #f1f5f9;
	background: #fff;
	box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05);
`;

const CrmEyebrow = styled.p`
	margin: 0 0 0.65rem;
	font-size: 0.625rem;
	font-weight: 800;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.14em;
`;

const StatHighlight = styled.div`
	display: flex;
	align-items: baseline;
	gap: 0.35rem;
	margin-bottom: 0.85rem;
	padding: 0.65rem 0.85rem;
	border-radius: 1rem;
	background: #f8fafc;
	border: 1px solid #f1f5f9;
`;

const StatHighlightVal = styled.span`
	font-size: 1.35rem;
	font-weight: 800;
	color: #4f46e5;
	letter-spacing: -0.02em;
`;

const StyledFieldInput = styled(Input)`
	&& {
		border-radius: 1rem;
		border: 2px solid #f1f5f9;
		font-weight: 600;
		font-size: 0.875rem;
	}
	&&:focus {
		border-color: #a5b4fc;
		box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
	}
`;

const NotesArea = styled.textarea`
	width: 100%;
	border-radius: 1rem;
	border: 2px solid #f1f5f9;
	padding: 0.75rem 0.85rem;
	font-weight: 600;
	font-size: 0.875rem;
	min-height: 88px;
	resize: vertical;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	&:focus {
		outline: none;
		border-color: #a5b4fc;
		box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
	}
`;

@inject("store") @observer
export class RestaurantCustomerModalClass extends MobxComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = { tagsSelected: [], newTagDraft: "", notesDraft: "" };
	}

	normalizeCustomerTags(raw: string | string[] | undefined): string[] {
		if (!raw) return [];
		if (Array.isArray(raw)) {
			return [...new Set(raw.map((t) => String(t).trim()).filter(Boolean))];
		}
		return [
			...new Set(
				raw
					.split(",")
					.map((s) => s.trim())
					.filter(Boolean)
			),
		];
	}

	query = async () => {
		try {
			const { store } = this.injected;
			const { query } = store.router.s;
			const { customer } = store;
			const queryButNoCustomer = query._id && !customer;
			const queryCustomerMismatch = query._id && customer && customer._id !== query._id;
			if (queryButNoCustomer || queryCustomerMismatch) {
				const _id = query._id;
				const response = await store.api.customer_find({
					_id,
					restaurant_id: store.restaurant!._id,
				});
				if (response.outcome) {
					store.router.push(`/restaurant/${store.restaurant!._id}/customers`);
					store.setCustomer(null);
					UI.notification.error(response.message);
				}
				else {
					store.setCustomer(response.item);
					this.setState({
						tagsSelected: this.normalizeCustomerTags(
							response.item.dashboard?.tags
						),
						newTagDraft: "",
						notesDraft: response.item.dashboard?.notes ?? "",
					});
				}
			}
		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("Error finding customer, please try again", { timeout: 5000 });
		}
	}

	close = () => {
		const { store } = this.injected;
		const r = store.restaurant!;
		store.router.push(`/restaurant/${r._id}/customers`);
		store.setCustomer(null);
	}

	clearSelectInput = (id: string) => {
		const el = document.getElementById(id) as HTMLSelectElement | null;
		if (el) {
			el.value = "";
		}
	}

	scheduleDashboardSave = (
		item: T.Schema.Customer.CustomerSchema,
		fields: { tags?: string[]; notes?: string }
	) => {
		const id = `cust-dash-${item._id}`;
		const w = window as unknown as { __cwCustTimers?: Record<string, number> };
		if (!w.__cwCustTimers) w.__cwCustTimers = {};
		if (w.__cwCustTimers[id]) window.clearTimeout(w.__cwCustTimers[id]);
		w.__cwCustTimers[id] = window.setTimeout(async () => {
			const { store } = this.injected;
			try {
				Untrusive.start();
				const rid = store.restaurant!._id;
				const res = await store.api.customer_update_dashboard({
					_id: item._id,
					restaurant_id: rid,
					...fields,
				});
				if (res.outcome !== 0) {
					throw new Error((res as { message?: string }).message || "save_failed");
				}
				if ("item" in res && res.item) {
					store.setCustomer(res.item);
				}
				if (fields.tags && store.restaurant) {
					const prev = store.restaurant.settings.customer_tags ?? [];
					const merged = [...new Set([...prev, ...fields.tags])];
					store.updateRestaurant({
						settings: {
							...store.restaurant.settings,
							customer_tags: merged,
						},
					});
				}
			} catch {
				UI.notification.error("Could not save customer notes");
			} finally {
				Untrusive.stop();
			}
		}, 450);
	};

	addCustomTag = (c: T.Schema.Customer.CustomerSchema) => {
		const raw = this.state.newTagDraft.trim();
		if (!raw) return;
		const exists = this.state.tagsSelected.some(
			(x) => x.toLowerCase() === raw.toLowerCase()
		);
		if (exists) {
			this.setState({ newTagDraft: "" });
			return;
		}
		const tagsSelected = [...this.state.tagsSelected, raw];
		this.setState({ tagsSelected, newTagDraft: "" });
		this.scheduleDashboardSave(c, { tags: tagsSelected });
	};

	handleActionSelect = async (item: T.Schema.Customer.CustomerSchema, value: string) => {

		this.clearSelectInput("customer-action-select");

		if (!value) {
			return;
		}

		const { store } = this.injected;

		try {

			if (value === "edit-email") {
				const login = prompt("Enter a new e-mail address for the customer");
				if (login === null) { }
				else if (login === "") {
					UI.notification.error("Please enter an e-mail address");
				}
				else {
					Untrusive.start();
					const { _id } = item;
					const response = await store.api.customer_update_email({ _id, login });
					if (response.outcome) {
						UI.notification.error(response.message, { timeout: 6000 });
					}
					else {
						const newCustomer = cloneDeepSafe(item);
						newCustomer.login = login;
						newCustomer.details.email = login;
						store.updateCustomerComplete(newCustomer);
						UI.notification.success("Customer e-mail updated");
					}
				}
			}

			if (value === "edit-password") {
				const password = prompt("Enter a new password for the customer. Minimum 6 characters. This will log the customer out");
				if (password === null) { }
				else if (password.length < 6 || password.length > 1024) {
					UI.notification.error("Minimum password length 6 characters");
				}
				else {
					Untrusive.start();
					const { _id } = item;
					const response = await store.api.customer_update_password({ _id, password });
					if (response.outcome) {
						UI.notification.error(response.message, { timeout: 6000 });
					}
					else {
						UI.notification.success("Customer password updated");
					}
				}
			}

			if (value === "delete") {
				const proceed = confirm("Once deleted, it cannot be recovered. Delete the customer?");
				if (!proceed) { }
				else {
					Untrusive.start();
					const { _id } = item;
					const restaurant_id = store.restaurant!._id;
					const response = await store.api.customer_delete({ _id, restaurant_id });
					if (response.outcome) {
						UI.notification.error(response.message, { timeout: 6000 });
					}
					else {
						this.close();
						store.removeCustomer(_id);
						UI.notification.success("Customer deleted");
					}
				}
			}

		}
		catch (e) {
			logger.captureException(e);
			UI.notification.error("An error occurred, try again soon or contact us", { timeout: 6000 });
		}
		finally {
			Untrusive.stop();
		}

	}

	/** Match cloudtable “Create New Reservation”: hand off guest to Bookings add modal. */
	openBookingsNewReservation = (c: T.Schema.Customer.CustomerSchema) => {
		const { store } = this.injected;
		const rid = store.restaurant!._id;
		try {
			sessionStorage.setItem(
				CW_BOOKING_PREFILL_STORAGE_KEY,
				JSON.stringify({
					name: c.details?.name || "",
					email: (c.details?.email || c.login || "").trim(),
					phone: (c.details?.phone || "").trim(),
				})
			);
		} catch {
			//
		}
		store.router.push(`/restaurant/${rid}/bookings`);
	};

	customerInitials(name: string, email: string) {
		const n = name.trim();
		if (n.length) {
			const parts = n.split(/\s+/).filter(Boolean);
			if (parts.length >= 2) {
				return (
					parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
				).toUpperCase();
			}
			return n.slice(0, 2).toUpperCase();
		}
		const e = email.trim();
		return e.length >= 2 ? e.slice(0, 2).toUpperCase() : "?";
	}

	render() {
		const { restrictions } = this.injected.store;
		const { store, t } = this.injected;
		const { query } = store.router.s;
		const _id = query._id || null;
		const item = store.customer;

		const actionSelectOptions: Array<{ label: string; value: string }> = [];

		if (restrictions.restaurant.customers_actions) {

			if (item && item.type === "email") {

				actionSelectOptions.push({
					label: "Edit e-mail address",
					value: "edit-email",
				});

				actionSelectOptions.push({
					label: "Edit password",
					value: "edit-password",
				});

			}

			actionSelectOptions.push({
				label: "Delete customer",
				value: "delete",
			});

		}

		return (
			<RestaurantItemModal<T.Schema.Customer.CustomerSchema>
				active={!!_id}
				item={item}
				query={this.query}
				close={this.close}>
				{(c) => (
					<div>

						<HeroShell>
							<HeroTop>
								{c.avatar ? (
									<AvatarTile style={{ padding: 0 }}>
										{/* @ts-ignore */}
										<img
											src={c.avatar}
											alt=""
											style={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
											}}
										/>
									</AvatarTile>
								) : (
									<AvatarTile>
										{this.customerInitials(
											c.details.name,
											c.details.email || c.login || ""
										)}
									</AvatarTile>
								)}
								<HeroTitleBlock>
									<HeroName>
										<span className="notranslate">
											{c.details.name || c.details.email || c.login}
										</span>
									</HeroName>
									<HeroMeta>
										<VerifiedPill>
											{c.verified
												? t("Verified", { defaultValue: "Verified" })
												: t("Unverified", { defaultValue: "Unverified" })}
										</VerifiedPill>
										<span
											className="tiny text-muted"
											style={{
												fontWeight: 700,
												textTransform: "uppercase",
												letterSpacing: "0.1em",
											}}
										>
											{c.type.charAt(0).toUpperCase() + c.type.slice(1)}
										</span>
									</HeroMeta>
								</HeroTitleBlock>
							</HeroTop>
							<ReserveCta
								type="button"
								onClick={() => this.openBookingsNewReservation(c)}
							>
								<FaCalendarAlt size={16} aria-hidden />
								{t("Create new reservation", {
									defaultValue: "Create new reservation",
								})}
							</ReserveCta>
						</HeroShell>

						{actionSelectOptions.length > 0 && (
							<Select
								id="customer-action-select"
								className="no-round no-border-lr"
								placeholder={t("Actions", { defaultValue: "Actions" })}
								options={actionSelectOptions}
								onChange={(e) => this.handleActionSelect(c, e.target.value)}
							/>
						)}

						<ModalContent paddinglr={16} paddingtb={14}>
							<CrmCard>
								<CrmEyebrow>
									{t("CRM & preferences", { defaultValue: "CRM & preferences" })}
								</CrmEyebrow>
								{(c as { reservation_count?: number }).reservation_count !==
									undefined && (
									<StatHighlight>
										<span className="tiny text-muted font-bold text-uppercase">
											{t("# Reservations", { defaultValue: "# Reservations" })}
										</span>
										<StatHighlightVal>
											{t("number", {
												value:
													(c as { reservation_count?: number })
														.reservation_count ?? 0,
											})}
										</StatHighlightVal>
									</StatHighlight>
								)}
								<FormGroup
									optional
									title={t("Tags", { defaultValue: "Tags" })}
									help={t("customerTags.help", {
										defaultValue:
											"Choose saved tags or add a new one — new tags are saved for reuse on this restaurant.",
									})}
								>
									{(() => {
										const r = this.injected.store.restaurant!;
										const pool = [
											...new Set([
												...(r.settings.customer_tags ?? []),
												...this.state.tagsSelected,
											]),
										].sort((a, b) => a.localeCompare(b));
										const options = pool.map((tag) => ({
											label: tag,
											value: tag,
										}));
										return (
											<>
												<SelectAdv
													type="multi"
													shadow="one"
													placeholder={t("Select tags", {
														defaultValue: "Select tags",
													})}
													options={options}
													value={this.state.tagsSelected}
													onChange={(selected) => {
														this.setState({ tagsSelected: selected });
														this.scheduleDashboardSave(c, {
															tags: selected,
														});
													}}
												/>
												<div
													className="flex-line gap-2 m-t-2"
													style={{ alignItems: "stretch" }}
												>
													<Input
														value={this.state.newTagDraft}
														placeholder={t("New tag name", {
															defaultValue: "New tag name",
														})}
														onChange={(e) =>
															this.setState({
																newTagDraft: e.target.value,
															})
														}
														onKeyDown={(e) => {
															if (e.key === "Enter") {
																e.preventDefault();
																this.addCustomTag(c);
															}
														}}
													/>
													<Button
														type="button"
														color="primary"
														size="sm"
														onClick={() => this.addCustomTag(c)}
													>
														{t("Add", { defaultValue: "Add" })}
													</Button>
												</div>
											</>
										);
									})()}
								</FormGroup>
								<p className="tiny font-bold text-muted m-b-1">
									{t("Notes", { defaultValue: "Notes" })}
								</p>
								<div className="notranslate">
									<NotesArea
										value={this.state.notesDraft}
										placeholder={t("Internal notes", {
											defaultValue: "Internal notes",
										})}
										onChange={(e) => {
											const v = e.target.value;
											this.setState({ notesDraft: v });
											this.scheduleDashboardSave(c, { notes: v });
										}}
									/>
								</div>
							</CrmCard>

							<div
								className="m-b-2"
								style={{
									fontSize: "0.625rem",
									fontWeight: 800,
									color: "#94a3b8",
									textTransform: "uppercase",
									letterSpacing: "0.12em",
								}}
							>
								{t("Contact & account", { defaultValue: "Contact & account" })}
							</div>
							<DetailRowList
								items={[
									{ l: "Type", v: c.type.charAt(0).toUpperCase() + c.type.slice(1) },
									{ l: "Name", noTranslate: true, v: c.details.name },
									{ l: "Phone", noTranslate: true, v: c.details.phone || "N/A" },
									{ h: !c.login || c.login === c.details.email, l: "Login E-Mail", noTranslate: true, v: c.login },
									{ l: "E-Mail", noTranslate: true, v: c.details.email },
									{ l: "E-Mail Verified", v: c.verified ? "Yes" : "No" },
									{ l: "Created", v: t("datetimeFromTimestamp", { value: c.created }) },
									{ l: "Last Seen", v: c.meta?.last_seen ? t("datetimeFromTimestamp", { value: c.meta?.last_seen }) : "N/A" },
									{ l: "Last Order", v: c.meta?.last_order ? t("datetimeFromTimestamp", { value: c.meta?.last_order }) : "N/A" },
									{ l: "Last IP Address", noTranslate: true, v: (c.meta?.last_ip && c.meta?.last_ip.indexOf("::") === -1) ? c.meta?.last_ip : "N/A" },
									{ l: "Delivery Address", noTranslate: true, v: c.delivery?.destination || "N/A" },
								]}
							/>
						</ModalContent>

						<ModalDropContent
							title={
								<h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>
									{t("Statistics", { defaultValue: "Statistics" })}
								</h4>
							}
						>
							<DetailRowList
								items={[
									{ l: "Sessions", v: c.stats?.sessions },
									{ l: "Total Sales", v: t("currency", { value: c.stats?.sales }) },
									{ l: "Total Orders", v: c.stats?.orders },
									{ l: "Total Promos Used", v: c.stats?.promos_count },
								]}
							/>
						</ModalDropContent>

						<CustomerModalAgeVerification />

					</div>
				)}
			</RestaurantItemModal>
		);
	}

}

export const RestaurantCustomerModal = withTranslation()(RestaurantCustomerModalClass);
